import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Form, Modal } from 'react-bootstrap';
import { handleDelete, handleSubmit as modalSubmit } from '../../components/modals/ModalHelpers';
import DynModal from '../../components/modals/DynModal';
import ButtonCard from '../../components/cards/ButtonCard';
import ButtonGroup from '../../components/modals/ButtonGroup';
import InputField from '../../components/form/InputField';
import Validation from '../../utils/Validation';
import SingleSelect from '../../components/form/selects/SingleSelect';
import ValidationErrors from '../../utils/ValidationErrors';
import PagedTable from '../../components/table/PagedTable';
import { Renderers } from '../../components/table/Renderer';
import Bivema from '../../common/Bivema';
import getAllPagedForSelect from '../../api/GetAllPagedForSelect';
import { getSelfLink } from '../../utils/Utils';

const columnsRooms = [
  { title: 'Zimmernummer', name: 'number' },
  { title: 'Sitzplätze', name: 'capacity' },
  {
    title: 'Adresse',
    name: Bivema.ADDRESS,
    relation: Bivema.ADDRESS,
    renderer: Renderers.Address,
  },
  { title: 'E-Mail', name: 'email' },
  { title: 'Edit', name: '_links.self.href' },
];

const defaultRoom = {
  number: '', capacity: '', email: '', address: '',
};

const roomValidationSchema = Yup.object().shape({
  number: Yup.string()
    .max(50, 'Darf maximal 50 Zeichen lang sein.')
    .required('Bitte geben Sie eine Zimmernummer ein.'),
  capacity: Yup.number()
    .nullable(true)
    .typeError('Bitte geben Sie eine Zahl ein.')
    .moreThan(-1, 'Bitte geben Sie eine positive Zahl ein.'),
  email: Yup.string()
    .nullable(true)
    .max(100, 'Darf maximal 100 Zeichen lang sein.')
    .matches(Validation.email, ValidationErrors.email),
  address: Yup.string()
    .nullable(true),
});

const RoomsOverview = () => {
  const [update, setUpdate] = useState(false);
  const [selfLink, setSelfLink] = useState();
  const [addresses, setAddresses] = useState();
  const [showDynModal, setShowDynModal] = useState(false);
  const [classNameDynModal, setClassNameDynModal] = useState('dynModal');

  const [room, setRoom] = useState({
    ...defaultRoom,
  });

  const handleSetRoom = (value) => {
    const editRoom = {
      ...value,
      address: getSelfLink(value.address),
    };

    setRoom(editRoom);
  };

  // set Tab Title
  useEffect(() => {
    document.title = 'Raum | Verwaltung';
  }, []);

  // update addresses
  useEffect(() => {
    const getAddresses = async () => {
      const response = await getAllPagedForSelect('addresses');
      const result = response._embedded?.addresses
        .map((address) => ({ label: `${address.street} ${address.houseNumber}, ${address.city}`, value: address._links.address.href }));
      setAddresses(result);
    };

    if (addresses === undefined) {
      getAddresses();
    }
  }, []);

  // needed for formik, to pass it to the modal and set the values
  const formikRef = useRef();

  useEffect(() => {
    if (room._links !== undefined) {
      setSelfLink(room._links.self.href);
    }

    // sets formik values
    if (formikRef.current && room !== formikRef.current.values) {
      formikRef.current.setValues(room);
    }
  }, [room]);

  return (
    <div className="container-fluid">
      <ButtonCard title="Raum" className="mt-2" setShowDynModal={setShowDynModal}>
        <DynModal
          className={classNameDynModal}
          headerTitle="Raum"
          setEntity={setRoom}
          defaultEntity={defaultRoom}
          settersToReset={[setSelfLink]}
          formikRef={formikRef}
          show={showDynModal}
          setShow={setShowDynModal}
          modalBody={(

            <Formik
              innerRef={formikRef}
              initialValues={room}
              onSubmit={(values, submitProps) => modalSubmit(
                values,
                submitProps,
                setUpdate,
                Bivema.ROOMS,
                selfLink,
              )}
              validateOnChange={false} // because at the moment on change validates all fields
              validationSchema={roomValidationSchema}
            >
              {({
                errors, touched, handleSubmit, handleReset, handleChange, values, setFieldValue,
              }) => {
                return (
                  <Form onSubmit={handleSubmit} onReset={handleReset} noValidate>
                    <Modal.Body>
                      <Form.Group controlId="room-group">
                        <InputField
                          value={values.number}
                          handleChange={handleChange}
                          touched={touched.number}
                          errorDescription={errors.number}
                          fieldName="number"
                          placeholder="Zimmernummer*"
                          props={{ className: 'mb-2' }}
                        />
                        <InputField
                          value={values.email}
                          handleChange={handleChange}
                          touched={touched.email}
                          errorDescription={errors.email}
                          fieldName="email"
                          placeholder="Zimmer Email"
                          props={{ className: 'mb-2' }}
                        />
                        <SingleSelect
                          name="address"
                          placeholder="Adresse"
                          // pass object to defaultValue, so it be displayed in the select
                          value={addresses?.find((a) => a.value === values.address)}
                          values={addresses}
                          setFieldValue={setFieldValue}
                          touched={touched.address}
                          errorDescription={errors.address}
                        />
                        <InputField
                          value={values.capacity}
                          handleChange={handleChange}
                          touched={touched.capacity}
                          errorDescription={errors.capacity}
                          fieldName="capacity"
                          placeholder="Sitzplätze"
                          props={{ className: 'mb-2' }}
                        />
                      </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                      <ButtonGroup
                        handleDelete={() => handleDelete(selfLink, setUpdate)}
                        selfLink={selfLink !== undefined}
                        setShowDynModal={setShowDynModal}
                        setClassNameDynModal={setClassNameDynModal}
                      />
                    </Modal.Footer>
                  </Form>
                );
              }}
            </Formik>
              )}
        />
        <PagedTable
          entityName={Bivema.ROOMS}
          columns={columnsRooms}
          pageSize={10}
          setShowDynModal={setShowDynModal}
          setEntity={handleSetRoom}
          setUpdate={setUpdate}
          update={update}
        />
      </ButtonCard>
    </div>
  );
};

export default RoomsOverview;
